import { useState, useEffect } from 'react';
import { Shield, Lightbulb, FileText, Activity, BookOpen, Gauge, Mail, Bell, Brain, Mailbox, Plug } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router';
import UsersTab from '../components/admin/UsersTab';
import type { AdminUser } from '../components/admin/UsersTab';
import TipsTab from '../components/admin/TipsTab';
import PromptsTab from '../components/admin/PromptsTab';
import UsageTab from '../components/admin/UsageTab';
import ApiStatsTab from '../components/admin/api-stats/ApiStatsTab';
import BerichteTab from '../components/admin/BerichteTab';
import { GenerationLimitsTab } from '../components/admin/GenerationLimitsTab';
import KontaktTab from '../components/admin/KontaktTab';
import NewsletterTab from '../components/admin/NewsletterTab';
import KiBibliothekTab from '../components/admin/KiBibliothekTab';
import PostfaecherTab from '../components/admin/PostfaecherTab';
import Alert from '../components/admin/Alert';

type AdminTab = 'users' | 'usage' | 'api' | 'limits' | 'berichte' | 'tips' | 'prompts' | 'kontakt' | 'newsletter' | 'ki-bibliothek' | 'postfaecher';
const VALID_TABS: AdminTab[] = ['users', 'usage', 'api', 'limits', 'berichte', 'tips', 'prompts', 'kontakt', 'newsletter', 'ki-bibliothek', 'postfaecher'];

const TAB_DEFS: Array<{ id: AdminTab; label: string; Icon: typeof Shield }> = [
  { id: 'users',   label: 'Benutzer', Icon: Shield },
  { id: 'usage',    label: 'Nutzung',  Icon: Activity },
  { id: 'api',     label: 'API',      Icon: Plug },
  { id: 'limits',  label: 'Limits',   Icon: Gauge },
  { id: 'berichte', label: 'Berichte', Icon: BookOpen },
  { id: 'tips',     label: 'Tipps',    Icon: Lightbulb },
  { id: 'prompts', label: 'Prompts',  Icon: FileText },
  { id: 'kontakt',    label: 'Kontakt',    Icon: Mail },
  { id: 'newsletter', label: 'Newsletter', Icon: Bell },
  { id: 'ki-bibliothek', label: 'KI-Bibliothek', Icon: Brain },
  { id: 'postfaecher', label: 'Postfächer', Icon: Mailbox },
];

export default function Admin() {
  const { darkMode } = useDarkMode();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Initial tab from URL hash (e.g. /admin#tips)
  const initialTab: AdminTab = (typeof window !== 'undefined' && VALID_TABS.includes(window.location.hash.slice(1) as AdminTab))
    ? (window.location.hash.slice(1) as AdminTab)
    : 'users';
  const [activeTab, setActiveTabState] = useState<AdminTab>(initialTab);
  const [error, setError] = useState('');
  const [users, setUsers] = useState<AdminUser[]>([]);

  const setActiveTab = (tab: AdminTab) => {
    setActiveTabState(tab);
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', `#${tab}`);
    }
  };

  // Admin-Guard
  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/');
    }
  }, [user, navigate]);

  // React to hash changes after mount (Browser-Back/Forward)
  useEffect(() => {
    const onHashChange = () => {
      const next = window.location.hash.slice(1) as AdminTab;
      if (VALID_TABS.includes(next)) setActiveTabState(next);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const base = darkMode ? 'bg-slate-900 text-white min-h-screen' : 'bg-slate-100 text-slate-900 min-h-screen';

  return (
    <div className={`${base} p-6`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-600 rounded-lg">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Admin-Panel</h1>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Verwaltung</p>
          </div>
        </div>

        {error && (
          <Alert type="error" className="mb-4">
            {error}
            <button onClick={() => setError('')} className="ml-2 underline">Schließen</button>
          </Alert>
        )}

        <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-10">
          <aside className="md:w-56 md:flex-shrink-0">
            <nav className="md:sticky md:top-20 flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
              {TAB_DEFS.map(({ id, label, Icon }) => {
                const active = activeTab === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setActiveTab(id)}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap md:whitespace-normal text-left ${
                      active
                        ? darkMode
                          ? 'bg-blue-600 text-white'
                          : 'bg-blue-50 text-blue-700'
                        : darkMode
                          ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-white'
                    }`}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${active ? '' : 'opacity-70'}`} />
                    {label}
                  </button>
                );
              })}
            </nav>
          </aside>

          <div className="flex-1 min-w-0">
            {activeTab === 'users' && <UsersTab setError={setError} onUsersLoaded={setUsers} />}
            {activeTab === 'usage' && <UsageTab users={users} />}
            {activeTab === 'api' && <ApiStatsTab />}
            {activeTab === 'limits' && <GenerationLimitsTab />}
            {activeTab === 'berichte' && <BerichteTab users={users} />}
            {activeTab === 'tips' && <TipsTab setError={setError} />}
            {activeTab === 'prompts' && <PromptsTab setError={setError} />}
            {activeTab === 'kontakt' && <KontaktTab />}
            {activeTab === 'newsletter' && <NewsletterTab />}
            {activeTab === 'ki-bibliothek' && <KiBibliothekTab />}
            {activeTab === 'postfaecher' && <PostfaecherTab />}
          </div>
        </div>
      </div>
    </div>
  );
}
